import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { applicantAPI } from '@/services/api';
import { Briefcase, Sparkles, Info, CheckCircle2, Archive, X, Trophy, AlertCircle, Filter } from 'lucide-react';
import Pagination, { usePagination } from '@/components/shared/Pagination';

const STATE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active applications' },
  { value: 'completed', label: 'Completed (offer signed)' },
  { value: 'greyed', label: 'Closed / rejected / archived' },
];

export default function ApplicantJobMatches() {
  const nav = useNavigate();
  const [matches, setMatches] = useState([]);
  const [filter, setFilter] = useState({ state: '', min_match: '', start_date: '', end_date: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pg = usePagination(matches, 20);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = Object.fromEntries(Object.entries(filter).filter(([_, v]) => v !== '' && v !== null && v !== undefined));
      // applicantAPI.getJobMatches doesn't accept params; call the raw axios layer if needed
      const r = await applicantAPI.getJobMatches(params);
      setMatches(r.matches || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const apply = (e) => { e.preventDefault(); load(); };
  const clear = () => setFilter({ state: '', min_match: '', start_date: '', end_date: '' });

  return (
    <div data-testid="applicant-matches-page">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white tracking-tight">Job Matches</h1>
        <p className="text-slate-500 dark:text-zinc-500 text-sm">{matches.length} total</p>
      </div>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">All vector matches your profile has received. Grey rows are closed/rejected/archived; the green trophy marks fully signed offers.</p>

      {/* Filters */}
      <form onSubmit={apply} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-3 mb-4 flex flex-wrap gap-2 text-xs" data-testid="matches-filters">
        <select value={filter.state} onChange={e => setFilter({ ...filter, state: e.target.value })} className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded px-3 py-1.5 text-slate-900 dark:text-white text-sm" data-testid="filter-state">
          {STATE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <input type="number" min="0" max="100" placeholder="Min match %" value={filter.min_match} onChange={e => setFilter({ ...filter, min_match: e.target.value })} className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded px-3 py-1.5 text-sm text-slate-900 dark:text-white w-32" data-testid="filter-min-match" />
        <input type="date" value={filter.start_date} onChange={e => setFilter({ ...filter, start_date: e.target.value })} className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded px-3 py-1.5 text-sm text-slate-900 dark:text-white" data-testid="filter-start-date" />
        <input type="date" value={filter.end_date} onChange={e => setFilter({ ...filter, end_date: e.target.value })} className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded px-3 py-1.5 text-sm text-slate-900 dark:text-white" data-testid="filter-end-date" />
        <button type="submit" className="px-3 py-1 bg-slate-900 dark:bg-white text-white dark:text-black rounded text-sm font-medium flex items-center gap-1.5" data-testid="apply-match-filters"><Filter className="w-3 h-3" /> Apply</button>
        <button type="button" onClick={clear} className="px-3 py-1 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded text-sm">Clear</button>
      </form>

      {error && <div className="mb-4 px-4 py-2 rounded bg-red-500/10 text-red-600 dark:text-red-400 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}

      {loading ? <p className="text-slate-500 dark:text-zinc-500 text-sm">Loading matches…</p> : matches.length === 0 ? (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-10 text-center" data-testid="no-matches">
          <Briefcase className="w-10 h-10 text-slate-600 dark:text-zinc-700 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-zinc-500">No matches found for these filters.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pg.pageItems.map(m => (
            <MatchCard key={m.id} m={m} onOpen={() => nav(`/applicant/journey/${m.id}`)} />
          ))}
        </div>
      )}

      <Pagination {...pg.paginationProps} testIdPrefix="applicant-matches-pg" />
    </div>
  );
}

function MatchCard({ m, onOpen }) {
  const scoreColor = m.match_percentage >= 80 ? 'text-emerald-600 dark:text-emerald-400' : m.match_percentage >= 60 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500 dark:text-zinc-400';
  const baseCls = m.greyed_out
    ? 'bg-white dark:bg-[#0c0c0e] border-slate-200 dark:border-zinc-900/70 opacity-60'
    : m.is_signed
      ? 'bg-gradient-to-r from-emerald-500/10 to-emerald-500/5 border-emerald-500/30'
      : 'bg-white dark:bg-[#111114] border-slate-200 dark:border-zinc-800/50';

  return (
    <div className={`border rounded-xl p-4 transition ${baseCls}`} data-testid={`match-card-${m.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {m.is_signed && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 font-semibold" data-testid={`signed-pill-${m.id}`}>
                <Trophy className="w-3 h-3" /> Offer signed
                <span className="ml-1 w-2 h-2 rounded-full bg-emerald-400 animate-pulse" data-testid={`signed-bulb-${m.id}`} />
              </span>
            )}
            {!m.is_signed && m.is_completed && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="w-3 h-3" /> Selected</span>
            )}
            {m.greyed_out && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-200 dark:bg-zinc-700/40 text-slate-500 dark:text-zinc-400" data-testid={`grey-pill-${m.id}`}>
                {m.job_status === 'archived' ? <Archive className="w-3 h-3" /> : <X className="w-3 h-3" />}
                {m.job_status === 'archived' ? 'Archived' : m.job_status === 'closed' ? 'Closed' : 'Not selected'}
              </span>
            )}
          </div>
          <h3 className="text-slate-900 dark:text-white font-medium">{m.job_title}</h3>
          <p className="text-slate-500 dark:text-zinc-500 text-xs mt-0.5">{m.job_type || 'permanent'} · created {(m.created_at || '').slice(0, 10)}</p>
          {m.job_description && <p className="text-slate-500 dark:text-zinc-400 text-xs mt-2 line-clamp-2">{m.job_description}</p>}

          {m.greyed_out && m.grey_reason && (
            <div className="mt-3 flex items-start gap-2 text-xs text-slate-500 dark:text-zinc-400 bg-slate-50 dark:bg-zinc-900/40 border border-slate-200 dark:border-zinc-800/70 rounded p-2" data-testid={`grey-reason-${m.id}`}>
              <span className="relative group inline-flex shrink-0">
                <Info className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-500" />
              </span>
              <span><span className="text-slate-500 dark:text-zinc-500">Why is this greyed out?</span> {m.grey_reason}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className={`text-3xl font-semibold ${scoreColor}`}>{Math.round(m.match_percentage)}%</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-zinc-500 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI match</div>
          <button onClick={onOpen} disabled={m.greyed_out} className="text-xs px-3 py-1.5 rounded bg-slate-900 dark:bg-white text-white dark:text-black font-medium disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`view-journey-${m.id}`}>View journey</button>
        </div>
      </div>
    </div>
  );
}
